import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLandingData } from "../../../hooks/useLandingData";

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const { navLinks } = useLandingData();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNav = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };

  const toggleLanguage = () => {
    const next = i18n.language === "es" ? "en" : "es";
    i18n.changeLanguage(next);
  };

  const isEn = i18n.language === "en";

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/95 backdrop-blur-xl border-b border-stone-100"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-18 py-4">
          <a href="/" className="flex items-center gap-3 cursor-pointer">
            <img
              src="https://storage.readdy-site.link/project_files/eabe49ed-6fb1-4abe-9d8b-950f8fdeaeed/20102fc6-5b65-4c43-8938-3a6df6c13095_21-mar-2026-02_01_49.png?v=96141c8a356bac035317d0f45aa9e568"
              alt="Auria Logo"
              className="h-9 w-auto"
            />
            <span
              className={`text-xl font-bold tracking-tight transition-colors duration-300 ${
                scrolled ? "text-stone-900" : "text-white"
              }`}
            >
              Auria
            </span>
          </a>

          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => handleNav(link.href)}
                className={`text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                  scrolled
                    ? "text-stone-600 hover:text-stone-900"
                    : "text-white/80 hover:text-white"
                }`}
              >
                {link.label}
              </button>
            ))}

            {/* Mi cuenta button */}
            <Link
              to="/cuenta"
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                scrolled
                  ? "bg-stone-900 text-white hover:bg-stone-700"
                  : "bg-white/15 text-white hover:bg-white/25 border border-white/30"
              }`}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-user-line text-sm" />
              </div>
              {isEn ? "My account" : "Mi cuenta"}
            </Link>

            {/* Language toggle */}
            <button
              onClick={toggleLanguage}
              title={isEn ? "Cambiar a español" : "Switch to English"}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-bold tracking-wide transition-all duration-200 cursor-pointer whitespace-nowrap ${
                scrolled
                  ? "border-stone-200 text-stone-600 hover:border-stone-400 hover:text-stone-900"
                  : "border-white/30 text-white/80 hover:border-white/60 hover:text-white"
              }`}
            >
              <i className="ri-translate-line text-sm" />
              {isEn ? "ES" : "EN"}
            </button>
          </div>

          <div className="md:hidden flex items-center gap-3">
            {/* Mobile language toggle */}
            <button
              onClick={toggleLanguage}
              className={`text-xs font-bold px-2.5 py-1 rounded-full border transition-all cursor-pointer whitespace-nowrap ${
                scrolled
                  ? "border-stone-200 text-stone-600"
                  : "border-white/30 text-white/80"
              }`}
            >
              {isEn ? "ES" : "EN"}
            </button>

            <button
              className="w-9 h-9 flex items-center justify-center cursor-pointer"
              onClick={() => setMenuOpen(!menuOpen)}
            >
              <i
                className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-2xl ${
                  scrolled ? "text-stone-800" : "text-white"
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden bg-white border-b border-stone-100 px-6 py-4 flex flex-col gap-4">
          {navLinks.map((link) => (
            <button
              key={link.href}
              onClick={() => handleNav(link.href)}
              className="text-left text-sm font-medium text-stone-700 hover:text-stone-900 cursor-pointer whitespace-nowrap"
            >
              {link.label}
            </button>
          ))}
          {/* Mi cuenta – mobile */}
          <Link
            to="/cuenta"
            onClick={() => setMenuOpen(false)}
            className="flex items-center gap-2 text-sm font-semibold text-stone-900 hover:text-amber-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-user-line text-base" />
            </div>
            {isEn ? "My account" : "Mi cuenta"}
          </Link>
        </div>
      )}
    </nav>
  );
}
